import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Modal, 
  FormControl, 
  FormControlLabel, 
  FormLabel, 
  RadioGroup, 
  Radio, 
  Switch, 
  Paper,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCalculator } from '../../../contexts/CalculatorContext';

/**
 * Modal component for Call Tracking settings
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.callTrackingType - Current call tracking type
 * @param {boolean} props.callMeButtonEnabled - Whether Call Me Button is enabled
 * @param {number} props.staticSources - Number of static sources
 * @param {number} props.callTrackingSessions - Number of call tracking sessions
 * @param {number} props.numberFormatsQuantity - Number of phone number formats
 * @param {Function} props.onApply - Function to apply settings
 */
const CallTrackingSettingsModal = ({ 
  open, 
  onClose, 
  callTrackingType, 
  callMeButtonEnabled, 
  staticSources, 
  callTrackingSessions,
  numberFormatsQuantity = 1,
  onApply 
}) => {
  // Локальні стани для налаштувань, щоб зберігати зміни тільки після натискання "Apply"
  const [localType, setLocalType] = React.useState(callTrackingType);
  const [localCallMeButton, setLocalCallMeButton] = React.useState(callMeButtonEnabled);
  const [localStaticSources, setLocalStaticSources] = React.useState(staticSources);
  const [localNumberFormats, setLocalNumberFormats] = React.useState(numberFormatsQuantity);
  const theme = useTheme(); // Hook to get theme
  const { t } = useTranslation(); // Hook for translations
  
  // Get very custom product discounts from context
  const { veryCustomProductDiscounts, handleUpdateVeryCustomProductDiscount } = useCalculator();
  
  // Advanced Settings state
  const currentDiscount = veryCustomProductDiscounts['call_tracking'] || 0;
  const [isAdvancedMode, setIsAdvancedMode] = React.useState(currentDiscount > 0);
  const [localDiscount, setLocalDiscount] = React.useState(currentDiscount);
  const [discountString, setDiscountString] = React.useState(String(currentDiscount));
  
  // Оновлення локальних станів при зміні пропсів
  React.useEffect(() => {
    if (open) {
      setLocalType(callTrackingType);
      setLocalCallMeButton(callMeButtonEnabled);
      setLocalStaticSources(staticSources);
      setLocalNumberFormats(numberFormatsQuantity);
      const currentDiscount = veryCustomProductDiscounts['call_tracking'] || 0;
      setLocalDiscount(currentDiscount);
      setDiscountString(String(currentDiscount));
      setIsAdvancedMode(currentDiscount > 0);
    }
  }, [callTrackingType, callMeButtonEnabled, staticSources, numberFormatsQuantity, open, veryCustomProductDiscounts]);

  // Handle keyboard shortcuts for advanced mode
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Shift+C (PC) or Cmd+Shift+C (Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'C') {
        event.preventDefault();
        setIsAdvancedMode(prev => !prev);
      }
    };
    
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);
  
  // Handle double click on title for advanced mode
  const handleTitleDoubleClick = () => {
    setIsAdvancedMode(prev => !prev);
  };

  // Handle discount input changes
  const handleDiscountChange = (e) => {
    setDiscountString(e.target.value);
  };

  // Handle blur event - convert to number and validate (0-100%)
  const handleDiscountBlur = () => {
    const numValue = Number(discountString);
    if (isNaN(numValue) || numValue < 0) {
      setLocalDiscount(0);
      setDiscountString('0');
    } else if (numValue > 100) {
      setLocalDiscount(100);
      setDiscountString('100');
    } else {
      setLocalDiscount(numValue);
      setDiscountString(String(numValue));
    }
  };
  
  
  // Функція для застосування налаштувань
  const applySettings = () => {
    // Apply very custom product discount
    handleUpdateVeryCustomProductDiscount('call_tracking', localDiscount);
    
    // Check if there are any actual custom settings
    const hasAnyRealCustomSettings = (localType !== 'dynamic') || 
                                     localCallMeButton || 
                                     (localDiscount > 0) ||
                                     (localNumberFormats > 1);
    
    onApply({
      callTrackingType: localType,
      callMeButtonEnabled: localCallMeButton,
      staticSources: localStaticSources,
      numberFormatsQuantity: localNumberFormats,
      hasCustomSettings: hasAnyRealCustomSettings
    });
  };
  
  // Функція для скасування змін
  const cancelSettings = () => {
    onClose();
  };
  
  // Функція для скидання налаштувань до дефолтних
  const resetSettings = () => {
    setLocalType('dynamic');
    setLocalCallMeButton(false);
    setLocalStaticSources(0);
    setLocalNumberFormats(1);
  };
  
  return (
    <Modal 
      open={open} 
      onClose={cancelSettings}
      aria-labelledby="call-tracking-settings-title"
    >
      <Box sx={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)', 
        width: { xs: '90%', sm: 500 }, 
        bgcolor: 'background.paper', 
        boxShadow: 24, 
        p: 4,
        borderRadius: theme.spacing(1), // Updated radius
        outline: 'none'
      }}>
        <Typography 
          id="call-tracking-settings-title" 
          variant="h6" 
          component="h2" 
          sx={{ mb: 3, cursor: 'pointer', userSelect: 'none' }}
          onDoubleClick={handleTitleDoubleClick}
        >
          {t('calculator.callTrackingSettings', 'Call Tracking Settings')}
        </Typography>
        
        {/* Тип колтрекінгу */}
        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
          <FormLabel component="legend">{t('calculator.trackingType', 'Tracking Type')}</FormLabel>
          <RadioGroup 
            value={localType} 
            onChange={(e) => setLocalType(e.target.value)}
            name="tracking-type-group"
          >
            <FormControlLabel value="dynamic" control={<Radio />} label={t('calculator.dynamicTracking', 'Dynamic (based on sessions)')} />
            <FormControlLabel value="static" control={<Radio />} label={t('calculator.staticTracking', 'Static (fixed sources)')} />
            <FormControlLabel value="combined" control={<Radio />} label={t('calculator.combinedTracking', 'Combined (dynamic + static)')} />
          </RadioGroup>
        </FormControl>
        
        {/* Call Me Button */}
        <FormControlLabel 
          control={
            <Switch 
              checked={localCallMeButton} 
              onChange={(e) => setLocalCallMeButton(e.target.checked)} 
              color="primary"
            />
          }
          label={
            <Typography>{t('calculator.enableCallMeButton', 'Enable Call Me Button')}</Typography>
          }
          sx={{ mb: 3 }}
        />
        
        {/* Number Formats Quantity - only for dynamic or combined */}
        {(localType === 'dynamic' || localType === 'combined') && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="number-formats-label">
              {t('calculator.numberFormatsQuantity', 'Number formats quantity')}
            </InputLabel>
            <Select
              labelId="number-formats-label"
              value={localNumberFormats}
              onChange={(e) => setLocalNumberFormats(e.target.value)}
              label={t('calculator.numberFormatsQuantity', 'Number formats quantity')}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={5}>5</MenuItem>
            </Select>
          </FormControl>
        )}
        
        {/* Advanced Settings */}
        {isAdvancedMode && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Accordion 
              defaultExpanded 
              elevation={0}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: 'rgba(255, 152, 0, 0.08)',
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center' 
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#FF9800', fontWeight: 600 }}>
                  {t('calculator.advancedSettings', 'Advanced Settings')}
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails sx={{ pt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('calculator.veryCustomProductDiscount', 'Custom Product Discount')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('calculator.customDiscountNote', 'Applied after bundle discounts (0-100%)')}
                  </Typography>
                  
                  <TextField
                    size="small"
                    type="number"
                    value={discountString}
                    onChange={handleDiscountChange}
                    onBlur={handleDiscountBlur}
                    InputProps={{
                      endAdornment: '%',
                      inputProps: { min: 0, max: 100, step: 1 }
                    }}
                    sx={{ width: 120 }}
                    helperText={localDiscount > 0 ? `${localDiscount}% discount applied` : 'No discount'}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary">
                  {t('calculator.advancedSettingsHint', 'Access: Double-click title or Ctrl+Shift+C')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </>
        )}

        {/* Кнопки дій */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button onClick={resetSettings} color="inherit" variant="outlined">
            {t('calculator.resetToDefault', 'Reset to Default')}
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={cancelSettings} color="inherit">
              {t('calculator.cancel', 'Cancel')}
            </Button>
            <Button onClick={applySettings} variant="contained" sx={{ bgcolor: '#00C853', '&:hover': { bgcolor: '#00A040' } }}>
              {t('calculator.apply', 'Apply')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CallTrackingSettingsModal;
